import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Trash2, X, AlertCircle, DollarSign, Building2, CreditCard, Calendar, FileText, Banknote, Landmark, ScrollText, ReceiptText, Users, Copy } from 'lucide-react';
import { getPembayaranHutang, createPembayaranHutang, deletePembayaranHutang, getSupplierDebt, getSupplier, getHutangNota } from '../../api';
import { useAuth } from '../../AuthContext';
import RekapReimbursement from './RekapReimbursement';

const METODE_PEMBAYARAN = ['Tunai', 'Transfer', 'Cek/Giro', 'Lainnya'];

const INFO_ROW = {
  marginTop: 8, padding: '8px 12px', borderRadius: 6,
  background: 'white', border: '1px solid #bbf7d0',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

/**
 * BankInfo — informasi rekening bank supplier supaya admin bisa langsung
 * melakukan transfer saat membayar hutang. bank = null artinya data rekening
 * supplier belum diisi di master supplier.
 */
function BankInfo({ bank, copied, onCopy }) {
  if (!bank) {
    return (
      <div style={{
        marginTop: 8, padding: '8px 12px', borderRadius: 6,
        background: '#fffbeb', border: '1px solid #fde68a', fontSize: '0.75rem', color: '#b45309',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <AlertCircle size={13} />
        Rekening supplier belum diisi — lengkapi di menu Inventori → Master Supplier.
      </div>
    );
  }
  const nama = bank.nama_rekening || bank.nama || '';
  return (
    <div style={{
      marginTop: 8, padding: '10px 12px', borderRadius: 8,
      background: 'white', border: '1px solid #bbf7d0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Landmark size={14} style={{ color: '#16a34a' }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#166534', textTransform: 'uppercase' }}>
          Rekening Tujuan Transfer
        </span>
        {bank.nama_bank ? (
          <span style={{
            marginLeft: 'auto', fontSize: '0.7rem', fontWeight: 700, color: '#166534',
            background: '#dcfce7', border: '1px solid #a7f3d0', borderRadius: 12, padding: '1px 8px',
            textTransform: 'uppercase', letterSpacing: 0.4,
          }}>
            {bank.nama_bank}
          </span>
        ) : null}
      </div>
      {bank.no_rekening ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontSize: '1.05rem', fontWeight: 700, letterSpacing: 0.5, color: '#111827' }}>
              {bank.no_rekening}
            </span>
            <button
              type="button"
              onClick={() => onCopy(bank.no_rekening)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: copied ? '#dcfce7' : '#ecfdf5', border: '1px solid #a7f3d0',
                color: '#047857', borderRadius: 6, padding: '3px 8px',
                fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              <Copy size={12} /> {copied ? 'Tersalin!' : 'Salin'}
            </button>
          </div>
          <div style={{ fontSize: '0.78rem', color: '#4a7c59', marginTop: 2 }}>
            a.n. <strong>{nama || '-'}</strong>
            {bank.kode ? <span style={{ color: 'var(--gray-400)' }}> · {bank.kode}</span> : null}
          </div>
        </>
      ) : (
        <div style={{ fontSize: '0.78rem', color: '#b45309' }}>
          No. rekening belum diisi{nama ? ` (nama pemilik: ${nama})` : ''} — lengkapi di menu Inventori → Master Supplier.
        </div>
      )}
    </div>
  );
}

/**
 * SearchSelect — dropdown yang bisa dicari dengan mengetik.
 * Dipakai di modal Bayar Hutang: untuk memilih Supplier (mode "Per Supplier")
 * atau No. Pembelian / nota (mode "Per No. Pembelian").
 *
 * props:
 *  - options: [{ value, label, meta?, sub?, search? }]
 *  - value  : value yang dipilih
 *  - onChange(value)
 */
function SearchSelect({ options = [], value, onChange, placeholder = 'Cari & pilih...', emptyText = 'Tidak ada data', disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const selected = options.find(o => String(o.value) === String(value));
  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(o => String(o.search || o.label || '').toLowerCase().includes(q))
    : options;

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const pick = (o) => { onChange(o.value); setOpen(false); setQuery(''); };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={open ? query : (selected ? selected.label : '')}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => { setOpen(true); setQuery(''); setHighlight(0); }}
        onChange={e => { setOpen(true); setQuery(e.target.value); setHighlight(0); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, Math.max(filtered.length - 1, 0))); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight]); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 6,
          border: '1px solid #bbf7d0', fontSize: '0.85rem', background: 'white',
          boxSizing: 'border-box', color: 'var(--gray-700)',
        }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 60,
          background: '#fff', border: '1px solid #d1fae5', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.14)', maxHeight: 240, overflowY: 'auto',
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: '10px 12px', color: '#94a3b8', fontSize: '0.8rem' }}>
              {query.trim() ? `Tidak ada data cocok dengan "${query}"` : emptyText}
            </div>
          )}
          {filtered.slice(0, 100).map((o, i) => (
            <div
              key={o.value}
              onMouseDown={e => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: '0.82rem',
                background: i === highlight ? '#f0fdf4' : '#fff',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {o.label}
                </span>
                {o.meta && (
                  <span style={{ color: '#dc2626', fontSize: '0.75rem', whiteSpace: 'nowrap', fontWeight: 600 }}>
                    {o.meta}
                  </span>
                )}
              </div>
              {o.sub && <div style={{ color: '#94a3b8', fontSize: '0.72rem', marginTop: 2 }}>{o.sub}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PembayaranHutang() {
  const { hasMenuAction } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navConsumedRef = useRef(false);
  // Tab halaman: Hutang (nota supplier) · Reimbursement (nota yang ditalangi karyawan)
  const [pageTab, setPageTab] = useState('hutang');
  const [data, setData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierDebts, setSupplierDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10),
    supplier_name: '',
    jumlah: '',
    metode_pembayaran: 'Tunai',
    catatan: '',
  });
  // Mode bayar: 'supplier' (per supplier, lama) atau 'nota' (per No. Pembelian)
  const [mode, setMode] = useState('supplier');
  const [notaList, setNotaList] = useState([]);
  const [notaLoading, setNotaLoading] = useState(false);
  const [selectedNota, setSelectedNota] = useState(null);
  const [copiedRek, setCopiedRek] = useState(false);
  // Filter supplier opsional (dipakai saat masuk dari Ringkasan Hutang); '' = semua supplier
  const [supplierFilter, setSupplierFilter] = useState('');
  // Filter petugas opsional (dipakai saat masuk dari Rekap Reimbursement); '' = semua petugas
  const [petugasFilter, setPetugasFilter] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([getPembayaranHutang(), getSupplierDebt(), getSupplier()])
      .then(([res, debtRes, supRes]) => {
        setData(res.data || []);
        setSupplierDebts(debtRes.data || []);
        setSuppliers(supRes.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh supplier debts when modal opens
  useEffect(() => {
    if (showModal) {
      getSupplierDebt().then(res => setSupplierDebts(res.data || [])).catch(() => {});
    }
  }, [showModal]);

  // Dibuka dari halaman lain: buka modal Bayar Hutang langsung.
  //  - { supplier, mode:'nota' }            → mode nota, daftar nota difilter ke supplier itu
  //  - { mode:'nota', allSuppliers:true }   → mode nota tanpa filter (cari bebas by No. Pembelian)
  //  - { mode:'nota', petugas }             → mode nota, hanya nota reimbursement petugas tsb
  useEffect(() => {
    const st = location.state;
    if (!st || navConsumedRef.current) return;
    if (!st.supplier && !st.mode) return;
    navConsumedRef.current = true;
    const m = st.mode === 'nota' ? 'nota' : 'supplier';
    setMode(m);
    if (st.supplier) setForm(f => ({ ...f, supplier_name: st.supplier }));
    if (m === 'nota') {
      const petFilter = st.petugas || '';
      // petugas menang: tampilkan semua nota reimbursement orang tsb
      const supFilter = (st.allSuppliers || petFilter) ? '' : (st.supplier || '');
      setSupplierFilter(supFilter);
      setPetugasFilter(petFilter);
      loadNota(supFilter, petFilter);
    }
    setShowModal(true);
    // Bersihkan state agar tidak membuka modal lagi saat back/forward
    navigate(location.pathname, { replace: true });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  const getSupplierDebtAmount = (name) => {
    if (!name) return 0;
    const key = String(name).trim().toLowerCase();
    const match = supplierDebts.find(s =>
      String(s.supplier_name || '').trim().toLowerCase() === key
    );
    return match ? match.sisa_hutang : 0;
  };

  // Ambil daftar nota hutang belum lunas (Pembelian Langsung + Penerimaan PO).
  // supplierName kosong = semua supplier (mode cari berdasarkan No. Pembelian).
  // petugasName kosong = semua petugas; diisi = hanya nota reimbursement orang tsb.
  const loadNota = (supplierName, petugasName) => {
    setNotaLoading(true);
    getHutangNota(supplierName || undefined, petugasName || undefined)
      .then(res => setNotaList(res.data || []))
      .catch(() => setNotaList([]))
      .finally(() => setNotaLoading(false));
  };

  const pickSupplier = (name) => {
    setForm(f => ({ ...f, supplier_name: name }));
    setSelectedNota(null);
    if (mode === 'nota') loadNota(name, petugasFilter);
  };

  const pickNota = (val) => {
    if (!val) { setSelectedNota(null); return; }
    const [sumber, id] = String(val).split(':');
    const nota = notaList.find(n => n.sumber === sumber && String(n.nota_id) === String(id)) || null;
    setSelectedNota(nota);
    if (nota) {
      setForm(f => ({
        ...f,
        // Supplier otomatis ikut nota yang dipilih (daftar nota bisa lintas supplier)
        supplier_name: nota.supplier_name || f.supplier_name,
        jumlah: String(Math.round(Number(nota.sisa))),
        // Nota reimbursement: catatan diisi otomatis dengan nama yang menalangi dulu
        catatan: f.catatan || (nota.is_reimbursement
          ? (nota.nama_petugas ? `Reimbursement untuk ${nota.nama_petugas}` : 'Pembayaran reimbursement')
          : ''),
      }));
    }
  };

  const switchMode = (m) => {
    setError('');
    setMode(m);
    setSelectedNota(null);
    setForm(f => ({ ...f, jumlah: '' }));
    // Mode "Per No. Pembelian": tampilkan semua nota belum lunas supaya bisa langsung
    // dicari lewat No. Pembelian, tanpa harus memilih supplier terlebih dahulu.
    if (m === 'nota') { setSupplierFilter(''); setPetugasFilter(''); loadNota(''); }
  };

  const handleSave = async () => {
    setError('');
    if (!form.supplier_name.trim()) { setError('Nama supplier harus diisi'); return; }
    if (!form.jumlah || Number(form.jumlah) <= 0) { setError('Jumlah pembayaran harus > 0'); return; }
    if (mode === 'nota') {
      if (!selectedNota) { setError('Pilih No. Pembelian yang akan dibayar'); return; }
      if (Number(form.jumlah) > Number(selectedNota.sisa)) {
        setError(`Jumlah bayar melebihi sisa hutang nota ${selectedNota.no_nota} (${rupiah(selectedNota.sisa)})`);
        return;
      }
    }
    setSaving(true);
    try {
      await createPembayaranHutang({
        supplier_name: form.supplier_name,
        tanggal: form.tanggal,
        jumlah: Number(form.jumlah),
        metode_pembayaran: form.metode_pembayaran,
        catatan: form.catatan,
        ...(mode === 'nota' && selectedNota
          ? { sumber_nota: selectedNota.sumber, nota_id: selectedNota.nota_id }
          : {}),
      });
      setShowModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus pembayaran ini? Jurnal akuntansi terkait tidak otomatis dihapus.')) return;
    try { await deletePembayaranHutang(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  const openModal = () => {
    setError('');
    setMode('supplier');
    setNotaList([]);
    setSelectedNota(null);
    setSupplierFilter('');
    setPetugasFilter('');
    setForm({
      tanggal: new Date().toISOString().slice(0, 10),
      supplier_name: '',
      jumlah: '',
      metode_pembayaran: 'Tunai',
      catatan: '',
    });
    setShowModal(true);
  };

  // ---- Opsi dropdown (bergantung mode) ----
  const matchedSupplier = suppliers.find(s => s.nama === form.supplier_name);
  const supplierValue = matchedSupplier ? String(matchedSupplier.id) : (form.supplier_name ? '__manual__' : '');
  const supplierOptions = [
    ...(form.supplier_name && !matchedSupplier
      ? [{ value: '__manual__', label: `${form.supplier_name} (dari Ringkasan Hutang)`, search: form.supplier_name }]
      : []),
    ...suppliers.map(s => ({
      value: String(s.id),
      label: `${s.kode ? `[${s.kode}] ` : ''}${s.nama}`,
      search: `${s.kode || ''} ${s.nama}`,
      meta: getSupplierDebtAmount(s.nama) > 0 ? rupiah(getSupplierDebtAmount(s.nama)) : null,
    })),
  ];
  const notaOptions = notaList.map(n => ({
    value: `${n.sumber}:${n.nota_id}`,
    label: `${n.no_nota}${n.sumber === 'Penerimaan' ? ' (PO)' : ''}${n.is_reimbursement ? ' · Reimbursement' : ''}`,
    search: `${n.no_nota} ${n.supplier_name || ''} ${n.tanggal}${n.is_reimbursement ? ' reimbursement ' + (n.nama_petugas || '') : ''}`,
    meta: rupiah(n.sisa),
    sub: `${n.supplier_name || '-'} · ${n.tanggal}${n.is_reimbursement && n.nama_petugas ? ` · Ditalangi: ${n.nama_petugas}` : ''}`,
  }));

  // Data rekening bank supplier (dari master supplier) untuk keperluan transfer
  const getSupplierBank = (name) => {
    if (!name) return null;
    const key = String(name).trim().toLowerCase();
    const s = suppliers.find(x => String(x.nama || '').trim().toLowerCase() === key);
    if (!s) return null;
    if (!s.no_rekening && !s.nama_rekening && !s.nama_bank) return null;
    return { kode: s.kode || '', nama: s.nama, nama_bank: s.nama_bank || '', no_rekening: s.no_rekening || '', nama_rekening: s.nama_rekening || '' };
  };

  const copyRekening = async (no) => {
    try {
      await navigator.clipboard.writeText(no);
      setCopiedRek(true);
      setTimeout(() => setCopiedRek(false), 1600);
    } catch (err) {
      // clipboard tidak tersedia (mis. http) — abaikan, nomor tetap terlihat
    }
  };

  // Supplier aktif di modal (mode nota: ikut nota yang dipilih)
  const activeSupplierName = mode === 'nota' && selectedNota
    ? (selectedNota.supplier_name || form.supplier_name)
    : form.supplier_name;
  const bankTujuan = getSupplierBank(activeSupplierName);

  const handleSupplierSelect = (val) => {
    if (val === '__manual__') return; // nama dari Ringkasan Hutang (belum ada di master supplier)
    const sup = suppliers.find(s => String(s.id) === String(val));
    pickSupplier(sup ? sup.nama : '');
  };

  const clearSupplierFilter = () => {
    setSupplierFilter('');
    loadNota('', petugasFilter);
    if (!selectedNota) setForm(f => ({ ...f, supplier_name: '' }));
  };

  const clearPetugasFilter = () => {
    setPetugasFilter('');
    loadNota(supplierFilter, '');
  };

  const getMetodeIcon = (metode) => {
    switch (metode) {
      case 'Tunai': return <Banknote size={14} />;
      case 'Transfer': return <Landmark size={14} />;
      case 'Cek/Giro': return <ScrollText size={14} />;
      default: return <CreditCard size={14} />;
    }
  };

  const renderPageTabs = () => (
    <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
      {[{ key: 'hutang', label: 'Pembayaran Hutang', icon: DollarSign, color: '#16a34a' },
        { key: 'reimbursement', label: 'Reimbursement', icon: Users, color: '#7c3aed' }].map(t => {
        const Icon = t.icon;
        const aktif = pageTab === t.key;
        return (
          <button key={t.key} onClick={() => setPageTab(t.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
              cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
              border: `1px solid ${aktif ? t.color : '#e2e8f0'}`,
              background: aktif ? t.color : '#fff', color: aktif ? '#fff' : '#475569',
            }}>
            <Icon size={15} /> {t.label}
          </button>
        );
      })}
    </div>
  );

  // Tab Reimbursement: pakai halaman rekap reimbursement yang sudah ada
  if (pageTab === 'reimbursement') {
    return (
      <div className="page">
        {renderPageTabs()}
        <RekapReimbursement />
      </div>
    );
  }

  return (
    <div className="page">
      {renderPageTabs()}
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2><DollarSign size={20} style={{ marginRight: 8 }} />Pembayaran Hutang Supplier</h2>
            <p className="page-desc">{data.length} transaksi pembayaran</p>
          </div>
          {hasMenuAction('pembayaran_hutang', 'create') && (
            <button className="btn btn-primary" onClick={openModal}>
              <Plus size={16} /><span>Bayar Hutang</span>
            </button>
          )}
        </div>
      </div>

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th>
                <th>Tanggal</th>
                <th>Supplier</th>
                <th>No. Pembelian</th>
                <th>Jumlah</th>
                <th>Metode</th>
                <th>Catatan</th>
                {hasMenuAction('pembayaran_hutang', 'delete') && <th style={{ width: 60 }}>Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {data.map(d => (
                <tr key={d.id}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td style={{ fontWeight: 500 }}>{d.supplier_name}</td>
                  <td>
                    {d.no_nota ? (
                      <div>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem',
                          background: d.sumber_nota === 'Penerimaan' ? '#eff6ff' : '#f0fdf4',
                          color: d.sumber_nota === 'Penerimaan' ? '#2563eb' : '#16a34a',
                        }}>
                          {d.sumber_nota === 'Penerimaan' ? <ReceiptText size={13} /> : null}
                          {d.no_nota}
                        </span>
                        {d.is_reimbursement && (
                          <div
                            title={d.catatan_reimbursement || ''}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                              marginTop: 3, padding: '2px 6px', borderRadius: 4, fontSize: '0.68rem',
                              background: '#faf5ff', color: '#6d28d9', fontWeight: 600,
                            }}
                          >
                            <Landmark size={11} />
                            Reimbursement{d.nama_petugas ? `: ${d.nama_petugas}` : ''}
                          </div>
                        )}
                        {d.catatan_reimbursement && (
                          <div style={{ fontSize: '0.68rem', color: '#9333ea', marginTop: 2, maxWidth: 190 }}>
                            {d.catatan_reimbursement}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Per supplier</span>
                    )}
                  </td>
                  <td style={{ fontWeight: 600, color: '#16a34a' }}>{rupiah(d.jumlah)}</td>
                  <td>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem',
                      background: d.metode_pembayaran === 'Tunai' ? '#f0fdf4' :
                                  d.metode_pembayaran === 'Transfer' ? '#eff6ff' :
                                  d.metode_pembayaran === 'Cek/Giro' ? '#fefce8' : '#f5f5f4',
                      color: d.metode_pembayaran === 'Tunai' ? '#16a34a' :
                             d.metode_pembayaran === 'Transfer' ? '#2563eb' :
                             d.metode_pembayaran === 'Cek/Giro' ? '#ca8a04' : '#78716c',
                    }}>
                      {getMetodeIcon(d.metode_pembayaran)}
                      {d.metode_pembayaran}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.82rem', color: 'var(--gray-400)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.catatan || '-'}
                  </td>
                  {hasMenuAction('pembayaran_hutang', 'delete') && (
                    <td>
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                    </td>
                  )}
                </tr>
              ))}
              {data.length === 0 && (
                <tr><td colSpan={8} className="text-center text-muted" style={{ padding: 40 }}>Belum ada pembayaran hutang</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3><DollarSign size={18} style={{ marginRight: 8 }} />Bayar Hutang Supplier</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

              {/* Mode pembayaran: per supplier / per No. Pembelian */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {[
                  { key: 'supplier', label: 'Per Supplier', icon: <Users size={14} /> },
                  { key: 'nota', label: 'Per No. Pembelian', icon: <ReceiptText size={14} /> },
                ].map(m => (
                  <button key={m.key} type="button" onClick={() => switchMode(m.key)}
                    style={{
                      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '9px 10px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600,
                      border: `1px solid ${mode === m.key ? '#16a34a' : 'var(--gray-200)'}`,
                      background: mode === m.key ? '#16a34a' : '#fff',
                      color: mode === m.key ? '#fff' : 'var(--gray-500)',
                    }}>
                    {m.icon}{m.label}
                  </button>
                ))}
              </div>

              {/* Supplier Info Card */}
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                borderRadius: 10, padding: '12px 16px', marginBottom: 16,
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <Building2 size={16} style={{ color: '#16a34a' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.82rem', color: '#166534', textTransform: 'uppercase' }}>
                    Informasi Supplier
                  </span>
                </div>
                {mode === 'supplier' ? (
                  <>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Supplier</label>
                      <SearchSelect
                        options={supplierOptions}
                        value={supplierValue}
                        onChange={handleSupplierSelect}
                        placeholder="Cari supplier (nama / kode)..."
                        emptyText="Supplier tidak ditemukan"
                      />
                    </div>
                    {form.supplier_name && (
                      <div style={INFO_ROW}>
                        <span style={{ fontSize: '0.78rem', color: '#4a7c59' }}>Sisa Hutang</span>
                        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: getSupplierDebtAmount(form.supplier_name) > 0 ? '#dc2626' : '#16a34a' }}>
                          {rupiah(getSupplierDebtAmount(form.supplier_name))}
                        </span>
                      </div>
                    )}
                  </>
                ) : (

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>No. Pembelian / Nota Belum Lunas</label>
                    {supplierFilter && (
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                        marginBottom: 6, padding: '4px 8px', borderRadius: 6,
                        background: '#ecfdf5', border: '1px dashed #6ee7b7', fontSize: '0.72rem', color: '#047857',
                      }}>
                        <span>Dari Ringkasan Hutang: <strong>{supplierFilter}</strong></span>
                        <button type="button" onClick={clearSupplierFilter}
                          style={{ background: 'none', border: 'none', color: '#047857', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'underline', padding: 0 }}>
                          Tampilkan semua
                        </button>
                      </div>
                    )}
                    {petugasFilter && (
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                        marginBottom: 6, padding: '4px 8px', borderRadius: 6,
                        background: '#faf5ff', border: '1px dashed #d8b4fe', fontSize: '0.72rem', color: '#6d28d9',
                      }}>
                        <span>Reimbursement ditalangi: <strong>{petugasFilter}</strong></span>
                        <button type="button" onClick={clearPetugasFilter}
                          style={{ background: 'none', border: 'none', color: '#6d28d9', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, textDecoration: 'underline', padding: 0 }}>
                          Tampilkan semua nota
                        </button>
                      </div>
                    )}
                    {notaLoading ? (
                      <div style={{ fontSize: '0.8rem', color: '#4a7c59', padding: '6px 0' }}>Memuat nota...</div>
                    ) : notaList.length === 0 ? (
                      <div style={{
                        fontSize: '0.8rem', color: '#b45309', background: '#fffbeb',
                        border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px',
                      }}>
                        {supplierFilter
                          ? 'Tidak ada nota hutang yang belum lunas untuk supplier ini.'
                          : 'Belum ada nota hutang yang belum lunas.'}
                      </div>
                    ) : (
                      <SearchSelect
                        options={notaOptions}
                        value={selectedNota ? `${selectedNota.sumber}:${selectedNota.nota_id}` : ''}
                        onChange={pickNota}
                        placeholder="Cari No. Pembelian / supplier..."
                        emptyText="Nota tidak ditemukan"
                      />
                    )}
                    {selectedNota && (
                      <>
                        <div style={INFO_ROW}>
                          <span style={{ fontSize: '0.78rem', color: '#4a7c59' }}>Supplier</span>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {selectedNota.supplier_name || form.supplier_name}
                          </span>
                        </div>
                        <div style={INFO_ROW}>
                          <span style={{ fontSize: '0.78rem', color: '#4a7c59' }}>Sisa Nota {selectedNota.no_nota}</span>
                          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#dc2626' }}>
                            {rupiah(selectedNota.sisa)}
                          </span>
                        </div>
                        {selectedNota.is_reimbursement && (
                          <div style={{
                            marginTop: 8, padding: '8px 12px', borderRadius: 6,
                            background: '#faf5ff', border: '1px solid #e9d5ff',
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: '0.78rem', color: '#7c3aed', fontWeight: 600 }}>
                                Ditalangi oleh
                              </span>
                              <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6d28d9' }}>
                                {selectedNota.nama_petugas || '-'}
                              </span>
                            </div>
                            <div style={{ fontSize: '0.7rem', color: '#9333ea', marginTop: 4 }}>
                              Pembayaran ini akan mencatat pelunasan utang reimbursement karyawan (akun 2211),
                              bukan utang usaha ke supplier.
                            </div>
                            {selectedNota.catatan_reimbursement && (
                              <div style={{ fontSize: '0.75rem', color: '#6d28d9', marginTop: 6 }}>
                                <strong>Catatan:</strong> {selectedNota.catatan_reimbursement}
                              </div>
                            )}
                          </div>
                        )}
                        {getSupplierDebtAmount(selectedNota.supplier_name) > 0 && (
                          <div style={INFO_ROW}>
                            <span style={{ fontSize: '0.78rem', color: '#4a7c59' }}>Sisa Hutang Supplier</span>
                            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#dc2626' }}>
                              {rupiah(getSupplierDebtAmount(selectedNota.supplier_name))}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Rekening supplier — agar admin bisa langsung transfer */}
                {activeSupplierName && (
                  <BankInfo bank={bankTujuan} copied={copiedRek} onCopy={copyRekening} />
                )}
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label><Calendar size={13} style={{ marginRight: 4 }} />Tanggal</label>
                  <input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} />
                </div>
                <div className="form-group">
                  <label><CreditCard size={13} style={{ marginRight: 4 }} />Metode</label>
                  <select value={form.metode_pembayaran} onChange={e => setForm({ ...form, metode_pembayaran: e.target.value })}>
                    {METODE_PEMBAYARAN.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label><DollarSign size={13} style={{ marginRight: 4 }} />Jumlah Pembayaran</label>
                <input
                  type="number"
                  value={form.jumlah}
                  onChange={e => setForm({ ...form, jumlah: e.target.value })}
                  placeholder="0"
                  min="0"
                  style={{ fontWeight: 600, fontSize: '1.1rem' }}
                />
                {mode === 'nota' && selectedNota && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                    <small style={{ color: 'var(--gray-400)' }}>
                      Maksimal sisa nota: <strong style={{ color: '#dc2626' }}>{rupiah(selectedNota.sisa)}</strong>
                    </small>
                    <button type="button" className="btn btn-secondary"
                      style={{ fontSize: '0.72rem', padding: '3px 10px' }}
                      onClick={() => setForm(f => ({ ...f, jumlah: String(Math.round(Number(selectedNota.sisa))) }))}>
                      Bayar Penuh
                    </button>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label><FileText size={13} style={{ marginRight: 4 }} />Catatan (opsional)</label>
                <input type="text" value={form.catatan} onChange={e => setForm({ ...form, catatan: e.target.value })} placeholder="Keterangan pembayaran" />
              </div>

              {/* Preview Card */}
              {form.jumlah > 0 && form.supplier_name && (
                <div style={{
                  marginTop: 12, padding: '12px 16px', borderRadius: 8,
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                }}>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 500, marginBottom: 8 }}>
                    Ringkasan Pembayaran
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.82rem', color: '#475569' }}>Supplier</span>
                    <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{form.supplier_name}</span>
                  </div>
                  {mode === 'nota' && selectedNota && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: '0.82rem', color: '#475569' }}>No. Pembelian</span>
                      <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>
                        {selectedNota.no_nota}{selectedNota.sumber === 'Penerimaan' ? ' (PO)' : ''}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.82rem', color: '#475569' }}>Metode</span>
                    <span style={{ fontWeight: 500, fontSize: '0.82rem' }}>{form.metode_pembayaran}</span>
                  </div>
                  {bankTujuan && bankTujuan.no_rekening && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 8 }}>
                      <span style={{ fontSize: '0.82rem', color: '#475569' }}>Rekening Tujuan</span>
                      <span style={{ fontWeight: 600, fontSize: '0.82rem', textAlign: 'right' }}>
                        {bankTujuan.nama_bank ? `${bankTujuan.nama_bank} · ` : ''}{bankTujuan.no_rekening} · {bankTujuan.nama_rekening || bankTujuan.nama}
                      </span>
                    </div>
                  )}
                  <div style={{ borderTop: '1px solid #e2e8f0', margin: '6px 0', paddingTop: 6, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>Jumlah Dibayar</span>
                    <span style={{ fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>{rupiah(form.jumlah)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ background: '#16a34a', borderColor: '#16a34a' }}>
                {saving ? 'Menyimpan...' : <><DollarSign size={16} /> Konfirmasi Pembayaran</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
